/**
 * Basemap providers, and the details that differ between them.
 *
 * Three things vary per provider and each one fails silently when wrong, so
 * they are declared here rather than assumed:
 *
 *   **Tile axis order.** Most raster sources are `{z}/{x}/{y}`. The Esri
 *   MapServer services are `{z}/{y}/{x}` (level/row/column). Swapping them
 *   returns tiles for a different part of the world with no error at all.
 *
 *   **Tile size.** A retina tile is 512 px and must be declared as such, or
 *   the map renders it at the wrong scale and everything looks soft.
 *
 *   **Attribution.** Every one of these carries a licence condition requiring
 *   its credit to be shown. That credit is a legal obligation, not branding,
 *   and it belongs in the map's own attribution control. It is the one place
 *   a third-party name legitimately appears in this portal.
 *
 * Verified 13 Sep 2026 at 24.5762 N, 74.8066 E, zoom 15, by fetching one tile
 * from each and confirming it decodes to varied pixels rather than a flat
 * placeholder. Results are in each entry's `verified` note.
 */

import { apiUrl } from '../api/client';
export interface BasemapProvider {
  id: string;
  /** What a user sees in the picker. No vendor name. */
  label: string;
  /** One line on what it is good for. */
  note: string;
  /** Tile URL template. Placeholders are substituted by MapLibre. */
  url: string;
  /** 256 for standard tiles, 512 for retina. */
  tileSize: 256 | 512;
  maxzoom: number;
  /** Required by licence. Rendered in the map attribution control. */
  attribution: string;
  /** Environment variable holding the token, when the browser needs one. */
  tokenEnv?: string;
  /**
   * Credential held by the SERVER, not the browser. The tile URL points back
   * at this portal, which forwards each tile, so a billable key is never put
   * on the wire. `serverCredentials()` reports which of these are available.
   */
  serverCredential?: 'google' | 'ion';
  /** Result of the live probe, for anyone wondering whether this works. */
  verified: string;
}

/*
 * A tile template that points back at this portal has to carry the mount.
 * The portal is served from /field, and MapLibre requests these templates
 * verbatim - so a bare "/api/tiles/..." asked the ROOT of the host, which is a
 * different application entirely, and every Google or Cesium tile 404'd while
 * the provider still appeared in the picker. apiUrl() is a no-op at the root.
 */

/** Reads a Vite-exposed token, or an empty string when it is not set. */
function token(name: string): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return env[name] ?? '';
}

/**
 * Every provider, in the order they appear in the picker.
 *
 * The first that is usable becomes the default, so a deployment with no tokens
 * still gets a working satellite basemap.
 */
export const PROVIDERS: BasemapProvider[] = [
  {
    id: 'imagery',
    label: 'Satellite',
    note: 'Global high-resolution imagery. No key needed, so it always works.',
    // Level/row/column: this service is {z}/{y}/{x}, not the usual order.
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    tileSize: 256,
    maxzoom: 19,
    attribution:
      'Imagery &copy; Esri, Maxar, Earthstar Geographics and the GIS User Community',
    verified: '200, 17 KB JPEG, real imagery.',
  },
  {
    id: 'imagery-hd',
    label: 'Satellite, high detail',
    note: 'Sharper imagery where it is available. Needs the mapping token.',
    // Retina tiles at 512 px, standard {z}/{x}/{y} order.
    url: `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${token('VITE_MAPBOX_TOKEN')}`,
    tileSize: 512,
    maxzoom: 22,
    attribution: '&copy; Mapbox &copy; Maxar',
    tokenEnv: 'VITE_MAPBOX_TOKEN',
    verified: '200, 57 KB JPEG, real imagery, noticeably more detail than the default.',
  },
  {
    id: 'imagery-labelled',
    label: 'Satellite with roads',
    note: 'Imagery with roads and place names drawn over it.',
    url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/256/{z}/{x}/{y}?access_token=${token('VITE_MAPBOX_TOKEN')}`,
    tileSize: 256,
    maxzoom: 22,
    attribution: '&copy; Mapbox &copy; OpenStreetMap contributors',
    tokenEnv: 'VITE_MAPBOX_TOKEN',
    verified: '200, 16 KB JPEG, real imagery.',
  },
  {
    id: 'google-satellite',
    label: 'Google satellite',
    note: 'Google imagery. Often the most recent of the four over Indian farmland.',
    // Served through this portal so the billable key stays on the server.
    url: apiUrl('/api/tiles/google/satellite/{z}/{x}/{y}'),
    tileSize: 256,
    maxzoom: 22,
    attribution: '&copy; Google',
    serverCredential: 'google',
    verified: '200, 23 KB JPEG, real imagery. Identical bytes whether fetched direct or proxied.',
  },
  {
    id: 'google-hybrid',
    label: 'Google satellite with roads',
    note: 'Google imagery with roads and place names drawn over it.',
    url: apiUrl('/api/tiles/google/hybrid/{z}/{x}/{y}'),
    tileSize: 256,
    maxzoom: 22,
    attribution: '&copy; Google',
    serverCredential: 'google',
    verified: '200, real imagery.',
  },
  {
    id: 'google-roadmap',
    label: 'Road map',
    note: 'Plain road map, for finding a village rather than reading a field.',
    url: apiUrl('/api/tiles/google/roadmap/{z}/{x}/{y}'),
    tileSize: 256,
    maxzoom: 22,
    attribution: '&copy; Google',
    serverCredential: 'google',
    verified: '200, 5 KB PNG.',
  },
  {
    id: 'ion-aerial',
    label: 'Aerial, alternate',
    note: 'A second aerial source, useful when the others disagree about a field.',
    // Bing Maps Aerial through the ion asset, proxied like the rest.
    url: apiUrl('/api/tiles/ion/2/{z}/{x}/{y}'),
    tileSize: 256,
    maxzoom: 19,
    attribution: '&copy; Microsoft, &copy; Cesium ion',
    serverCredential: 'ion',
    verified: 'Asset resolves and reports IMAGERY; tiles served through the portal proxy.',
  },
];

/**
 * Which server-held credentials this deployment has.
 *
 * Filled once from /api/tiles/providers. Until that answers, providers needing
 * a server credential are treated as unavailable, so the picker never offers
 * one that would fail on its first tile.
 */
let serverHas: { google: boolean; ion: boolean } = { google: false, ion: false };

export async function loadServerCredentials(): Promise<void> {
  try {
    const res = await fetch(apiUrl('/api/tiles/providers'));
    if (!res.ok) return;
    const body = (await res.json()) as { data?: { google?: boolean; ion?: boolean } };
    serverHas = {
      google: Boolean(body.data?.google),
      ion: Boolean(body.data?.ion),
    };
  } catch {
    // Leaving them false is the safe answer: the picker simply offers fewer.
  }
}

/** True when the provider has everything it needs to draw. */
export function usable(p: BasemapProvider): boolean {
  if (!p.url) return false;
  if (p.tokenEnv && !token(p.tokenEnv)) return false;
  if (p.serverCredential && !serverHas[p.serverCredential]) return false;
  // A template whose token interpolated to empty would 401 on every tile.
  return !/access_token=(&|$)/.test(p.url);
}

export const availableProviders = (): BasemapProvider[] => PROVIDERS.filter(usable);

/** The first usable provider, so a deployment with no tokens still draws. */
export function defaultProvider(): BasemapProvider | null {
  return availableProviders()[0] ?? null;
}

export function providerById(id: string | null | undefined): BasemapProvider | null {
  if (!id) return null;
  const p = PROVIDERS.find((x) => x.id === id);
  return p && usable(p) ? p : null;
}
